if (!customElements.get('tpc-motion')) {
  customElements.define('tpc-motion', class extends HTMLElement {
    connectedCallback() {
      if (this.ready || !this.querySelector('video')) return;
      this.ready = true;
      this.video = this.querySelector('video');
      this.button = this.querySelector('[data-motion-toggle]');
      this.status = this.querySelector('[role="status"]');
      this.button.setAttribute('role', 'button');
      this.button.setAttribute('tabindex', '0');
      this.sync(false);
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.volume = 0;
      this.inView = false;
      this.userPaused = false;
      this.pending = 0;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.connection = navigator.connection;
      const toggle = () => {
        if (!this.video.paused) { this.userPaused = true; this.pause(); }
        else { this.userPaused = false; this.play(true); }
      };
      this.button.addEventListener('click', toggle, { signal });
      this.button.addEventListener('keydown', event => {
        if (event.key === ' ' || event.key === 'Enter') { event.preventDefault(); toggle(); }
      }, { signal });
      this.video.addEventListener('play', () => { this.completed = false; this.classList.toggle('has-started', true); this.sync(true); this.fadeFrame(); }, { signal });
      this.video.addEventListener('timeupdate', () => this.updateFade(), { signal });
      this.video.addEventListener('ended', () => { this.completed = true; this.explicitPlayback = false; this.sync(false); }, { signal });
      this.video.addEventListener('pause', () => this.sync(false), { signal });
      this.video.addEventListener('error', () => { this.pause(); this.status.textContent = this.status.dataset.unavailable; }, { signal });
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.pause(); else if (this.canAuto()) this.play(false); }, { signal });
      this.reduced.addEventListener('change', () => this.pause(), { signal });
      if (this.connection?.addEventListener) this.connection.addEventListener('change', () => this.pause(), { signal });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          for (const entry of entries) {
            this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.6;
            if (!this.inView) this.pause();
            else if (this.canAuto()) this.play(false);
          }
        }, { threshold: [0, 0.6] });
        this.observer.observe(this);
      } else this.inView = true;
    }
    canAuto() {
      return this.inView && !document.hidden && !this.userPaused && !this.completed && !this.reduced.matches && !this.connection?.saveData && !['slow-2g', '2g'].includes(this.connection?.effectiveType);
    }
    async play(explicit) {
      if (!explicit && !this.canAuto()) return;
      this.explicitPlayback = explicit;
      const ticket = ++this.pending;
      document.querySelectorAll('tpc-motion[data-has-video]').forEach(other => { if (other !== this && other.pause) other.pause(); });
      if (!this.loaded) {
        this.video.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
        this.video.load(); this.loaded = true;
      }
      if (this.video.ended) this.video.currentTime = 0;
      this.completed = false;
      this.video.muted = true; this.video.volume = 0;
      try {
        await this.video.play();
        if (ticket !== this.pending || document.hidden || (!explicit && !this.canAuto())) this.pause();
        else this.status.textContent = '';
      } catch (_) { this.sync(false); }
    }
    updateFade() {
      const duration = this.video.duration, time = this.video.currentTime;
      const fade = this.video.loop && Number.isFinite(duration) && duration > 1 && !this.reduced.matches ? Math.max(0, 1 - time / .32, 1 - (duration - time) / .48) : 0;
      this.style.setProperty('--tpc-fade', String(Math.min(1, fade)));
    }
    fadeFrame() {
      cancelAnimationFrame(this.fadeRequest);
      const tick = () => { this.updateFade(); if (!this.video.paused) this.fadeRequest = requestAnimationFrame(tick); };
      tick();
    }
    pause() { cancelAnimationFrame(this.fadeRequest); this.pending++; this.explicitPlayback = false; this.video?.pause(); this.sync(false); }
    sync(playing) {
      this.classList.toggle('is-playing', playing);
      this.classList.toggle('is-complete', Boolean(this.completed));
      if (!this.button) return;
      const text = playing ? this.button.dataset.pause : this.button.dataset.play;
      this.button.setAttribute('aria-label', text + ': ' + this.button.dataset.label);
      this.button.setAttribute('aria-pressed', String(playing));
    }
    disconnectedCallback() { this.pause(); this.abort?.abort(); this.observer?.disconnect(); this.ready = false; }
  });
}
